/**
 * Каркас экрана: шапка + прокручиваемое содержимое.
 *
 * §3.4 п. 4: прокручивается только содержимое; шапка, нижняя панель
 * и левая навигация имеют собственные слои и не перекрывают фокус.
 */
import { useNavigate, useRouter } from '@tanstack/react-router'
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  type UIEvent,
} from 'react'

import { useIsDesktopLayout } from '@shared/lib/use-media-query'
import { Icon, cx } from '@shared/ui'

import { useAppStore } from '../model/app-store'
import { hasInAppHistory, overrideNextDirection } from '../model/navigation-direction'
import { SCREEN_SCROLL_ATTR } from '../model/screen-scroll'
import { pushCoverSurface } from '../model/theme-color'
import { ScreenFrame, type ScreenFrameHandle } from './ScreenFrame'
import styles from './Screen.module.css'

/** Родительский экран для «назад» по deep link — когда предыдущей записи приложения нет. */
function parentPath(pathname: string, authenticated: boolean, search = ''): string {
  if (pathname.startsWith('/settings/')) return '/settings'
  // Профиль живёт внутри «Настроек» (шапка S-01), его подэкраны — внутри профиля
  if (pathname === '/me') return '/settings'
  if (pathname.startsWith('/me/')) return '/me'
  if (pathname.startsWith('/users/') || pathname.startsWith('/dvizh/')) return '/dvizh'
  if (pathname.startsWith('/chats/') || pathname.startsWith('/@')) return '/chats'
  // C-38: выбор собеседника для секретного чата открывается из «Чатов» (изменение v1.13).
  if (pathname.startsWith('/secret')) return '/chats'
  // Изменение v1.15: поиск открывается из вкладки и возвращается в неё же (`?scope=`).
  if (pathname === '/search') return searchScopeParent(search)
  if (pathname.startsWith('/notifications/')) return '/notifications'
  if (pathname.startsWith('/reports/') || pathname.startsWith('/appeals/')) return '/settings'
  // Изменение v1.15: документы — часть «Помощи и поддержки» (S-14 внутри S-15).
  if (pathname.startsWith('/legal/')) return authenticated ? '/settings/support' : '/'
  if (pathname === '/support/cant-login') return authenticated ? '/settings/support' : '/auth'
  return authenticated ? '/dvizh' : '/'
}

function searchScopeParent(search: string): string {
  const scope = new URLSearchParams(search).get('scope')
  if (scope === 'chats' || scope === 'settings' || scope === 'dvizh') return `/${scope}`
  return '/contacts'
}

/**
 * «Назад» как действие экрана, а не кнопки: шаг по истории, если запись
 * приложения есть, иначе — замена на родительский экран (deep link).
 *
 * Новой записью «назад» делать нельзя: после «Настройки → Изменить → назад»
 * стек становился `…/me/edit → /me`, и «назад» с профиля возвращал в форму —
 * из этой петли кнопками шапки было не выбраться (изменение v1.15).
 */
export function useGoBack(): () => void {
  const router = useRouter()
  const navigate = useNavigate()

  return useCallback(() => {
    if (hasInAppHistory()) {
      /*
       * Из открытого слоя («Не сохранять» в U-10) наверху — запись слоя
       * (`shared/lib/overlay-history.ts`). Два `back()` подряд — свой и снятие
       * записи слоем — Chromium выполняет оба, а WebKit только первый, и экран
       * оставался на месте. Поэтому один переход сразу через обе записи; слой
       * закрывается сам по этому же `popstate`.
       */
      const overlayOnTop = Boolean((window.history.state as { __overlay?: boolean } | null)?.__overlay)
      router.history.go(overlayOnTop ? -2 : -1)
      return
    }
    const { pathname, searchStr } = router.state.location
    overrideNextDirection('back')
    void navigate({
      to: parentPath(pathname, useAppStore.getState().phase === 'ready', searchStr),
      replace: true,
    })
  }, [navigate, router])
}

/** Запас до края обложки, после которого шапка становится сплошной. */
const COVER_SOLID_MARGIN_PX = 8

interface ScreenProps {
  title?: ReactNode
  subtitle?: ReactNode
  /** Показать кнопку «назад». `true` — вернуться в истории. */
  back?: boolean | (() => void)
  /**
   * U-10: пока задан, «назад» не уходит, а вызывает его (спросить про
   * несохранённое). Жест от края в это время выключен: он доигрывает уход до
   * конца и отменить его на полпути было бы нечем.
   */
  backGuard?: (() => void) | null
  /** Слева в шапке вместо «назад» — корневой экран с действием (QR в S-01). */
  leading?: ReactNode
  actions?: ReactNode
  /**
   * Обложка профиля над содержимым (изменение v1.15): шапка лежит поверх неё
   * прозрачной и становится сплошной, когда обложка уезжает под неё.
   */
  cover?: ReactNode
  /** Цвет верха обложки — для системной области, пока шапка прозрачная. */
  coverColor?: string | null
  /** Полоса под шапкой: фильтры, вкладки, поиск. */
  subbar?: ReactNode
  /** Элемент в самой шапке вместо заголовка (заголовок остаётся для скринридера). */
  headerContent?: ReactNode
  children: ReactNode
  /** Убрать поля содержимого — для списков во всю ширину. */
  flush?: boolean
  /** Ограничить содержимое 720 px (U-*, S-*, N-01). */
  narrow?: boolean
  /** Шапка на сплошном фоне — для экранов без списка под ней. */
  plainHeader?: boolean
  className?: string
  contentClassName?: string
  /** Область прокрутки — для восстановления позиции при возврате на экран. */
  contentRef?: RefObject<HTMLDivElement>
  onContentScroll?: (event: UIEvent<HTMLDivElement>) => void
}

export function Screen({
  title,
  subtitle,
  back,
  backGuard = null,
  leading,
  actions,
  cover,
  coverColor = null,
  subbar,
  headerContent,
  children,
  flush = false,
  narrow = false,
  plainHeader = false,
  className,
  contentClassName,
  contentRef,
  onContentScroll,
}: ScreenProps): React.ReactElement {
  const historyBack = useGoBack()
  const frameRef = useRef<ScreenFrameHandle>(null)
  const coverRef = useRef<HTMLDivElement>(null)
  const headerRef = useRef<HTMLElement>(null)
  /** Обложка ушла под шапку — шапка сплошная, заголовок виден. */
  const [solid, setSolid] = useState(false)

  /** Сам переход «назад» — его же вызывает жест от края после своей анимации. */
  const navigateBack = typeof back === 'function' ? back : historyBack

  /** Кнопка «назад»: сначала экран уезжает, потом происходит переход (ScreenFrame). */
  const goBack = (): void => {
    if (backGuard) {
      backGuard()
      return
    }
    if (frameRef.current) frameRef.current.pop(navigateBack)
    else navigateBack()
  }

  const isDesktop = useIsDesktopLayout()
  const hasHeader = title !== undefined || back || leading || actions || headerContent
  // На desktop обложка — карточка в колонке, шапка над ней обычная (Screen.module.css)
  const overlay = Boolean(cover) && Boolean(hasHeader) && !isDesktop

  const measureCover = useCallback((scrollTop: number): void => {
    const coverHeight = coverRef.current?.offsetHeight ?? 0
    const headerHeight = headerRef.current?.offsetHeight ?? 0
    const next = scrollTop > coverHeight - headerHeight - COVER_SOLID_MARGIN_PX
    setSolid((current) => (current === next ? current : next))
  }, [])

  // Возврат на экран восстанавливает прокрутку без события scroll — меряем и после неё.
  useEffect(() => {
    if (!overlay) return
    const frame = requestAnimationFrame(() => {
      const scroller = coverRef.current?.parentElement
      if (scroller) measureCover(scroller.scrollTop)
    })
    return () => cancelAnimationFrame(frame)
  }, [measureCover, overlay])

  // Пока шапка прозрачная, системная область — цвета обложки, а не белой шапки.
  useEffect(() => {
    if (!overlay || solid || !coverColor) return
    return pushCoverSurface(coverColor)
  }, [coverColor, overlay, solid])

  const handleScroll = (event: UIEvent<HTMLDivElement>): void => {
    if (overlay) measureCover(event.currentTarget.scrollTop)
    onContentScroll?.(event)
  }

  /*
   * Вложенный экран (с «назад») входит с той стороны, куда шёл переход, и
   * уходит жестом от края; корневые разделы и перезагрузка не анимируются.
   */
  return (
    <ScreenFrame
      ref={frameRef}
      nested={Boolean(back)}
      onBack={back && !backGuard ? navigateBack : undefined}
      className={cx(styles['screen'], className)}
    >
      {hasHeader ? (
        <header
          ref={headerRef}
          className={cx(
            styles['header'],
            plainHeader && styles['headerPlain'],
            overlay && styles['headerOverlay'],
            overlay && solid && styles['headerSolid'],
          )}
        >
          {back ? (
            <button type="button" className={styles['back']} onClick={goBack} aria-label="Назад">
              <Icon name="back" size={22} />
            </button>
          ) : null}
          {leading ? <div className={styles['leading']}>{leading}</div> : null}

          <div
            className={cx(styles['titleBlock'], Boolean(headerContent) && styles['titleBlockWithContent'])}
          >
            {title !== undefined ? (
              <h1 className={cx(styles['title'], Boolean(headerContent) && 'visually-hidden')}>{title}</h1>
            ) : null}
            {subtitle && !headerContent ? <div className={styles['subtitle']}>{subtitle}</div> : null}
            {headerContent ? <div className={styles['headerContent']}>{headerContent}</div> : null}
          </div>

          {actions ? <div className={styles['actions']}>{actions}</div> : null}
        </header>
      ) : null}

      {subbar ? <div className={styles['subbar']}>{subbar}</div> : null}

      <div
        ref={contentRef}
        className={cx(
          styles['content'],
          !flush && styles['contentPadded'],
          overlay && styles['contentWithCover'],
          contentClassName,
        )}
        onScroll={handleScroll}
        /* Позиция возвращается при возврате на экран (app/model/screen-scroll.ts) */
        {...{ [SCREEN_SCROLL_ATTR]: true }}
      >
        {cover ? (
          <div
            ref={coverRef}
            className={styles['cover']}
            style={{ '--screen-cover-color': coverColor ?? undefined } as CSSProperties}
          >
            {cover}
          </div>
        ) : null}
        {/* Ограничение ширины — на вложенном блоке, чтобы полоса прокрутки оставалась у края окна */}
        {narrow ? <div className={styles['narrow']}>{children}</div> : children}
      </div>
    </ScreenFrame>
  )
}
